// Unused or unreferenced check for delete
import type {
  EssentialPreferences,
  LoyaltyPreferences,
  Profile,
  UserPreference,
  PreferenceType,
  QuizResults,
  UserInsight,
  UserLoyaltyMembership,
  LoyaltyProgramCatalog,
  LoyaltyProgram,
  KeywordPreference,
  ProfileCompleteness,
  TravelerDimension,
  TravelerVector,
  TravelerProfile,
  AgentContext,
  QuizQuestionV2,
  EnrichedKeyword,
  KeywordSource,
  AgentProfile,
  ProfileDimension,
  MemoryAction,
  TravelerNote,
  NoteScope,
  TravelCompanion,
  Relationship,
  MemoryWipeResult,
  AccountDeleteResult,
  AccountRestoreResult,
} from '@/types/profile'

// Re-export for legacy callers; canonical home is `@/types/profile`.
export type { EnrichedKeyword, KeywordSource }
import { supabase } from '@/lib/supabase'
import { apiFetch } from '@/lib/api'
import { triggerAgentAnalysis } from '@/lib/agent-trigger'
import { generateRandomUsername } from '@/lib/utils'

async function generateUniqueUsername(): Promise<string> {
  const username = generateRandomUsername()
  const { data } = await supabase
    .from('profiles')
    .select('username')
    .eq('username', username)
    .single()

  if (data) {
    return generateUniqueUsername()
  }

  return username
}

export async function ensureProfile(userId: string | undefined | null) {
  if (!userId) {
    console.warn('[ensureProfile] Skipping: userId is undefined or null')
    return
  }

  try {
    // Get auth user to access email and metadata
    const { data: { user }, error: userError } = await supabase.auth.getUser()
    if (userError) {
      console.warn('[VOY-86] Could not fetch auth user:', userError)
    }

    const { data: existingProfile, error: fetchError } = await supabase
      .from('profiles')
      .select('id, username, email')
      .eq('id', userId)
      .single()

    if (fetchError && fetchError.code !== 'PGRST116') {
      throw fetchError
    }

    if (!existingProfile) {
      // New profile - populate with all available data
      const username = await generateUniqueUsername()
      
      // VOY-86: Extract name from OAuth metadata (Google, etc.)
      const fullName = user?.user_metadata?.full_name || user?.user_metadata?.name
      let firstName: string | null = null
      let lastName: string | null = null
      
      if (fullName && typeof fullName === 'string') {
        const nameParts = fullName.trim().split(' ').filter(part => part.length > 0)
        firstName = nameParts[0] || null
        
        if (nameParts.length > 1) {
          // Filter out middle initials (1-2 chars with period, e.g., "L.", "J.")
          // Keep everything else as last name
          const lastNameParts = nameParts.slice(1).filter(part => {
            // Keep if longer than 2 chars OR doesn't contain a period
            return part.length > 2 || !part.includes('.')
          })
          lastName = lastNameParts.length > 0 ? lastNameParts.join(' ') : null
        }
      }

      const { error: insertError } = await supabase
        .from('profiles')
        .insert({
          id: userId,
          username,
          email: user?.email || null,        // VOY-86: Copy email from auth
          name: firstName,                    // VOY-86: Extract from metadata
          last_name: lastName,                // VOY-86: Extract from metadata
          voy_coins: 100,
          voy_tokens: 0,
          role: 'user'
        })

      if (insertError) throw insertError
    } else {
      // Existing profile - update username if missing, backfill email if missing
      const updates: any = {}
      
      if (!existingProfile.username) {
        updates.username = await generateUniqueUsername()
      }
      
      // VOY-86: Backfill email for existing users without it
      if (!existingProfile.email && user?.email) {
        updates.email = user.email
      }
      
      if (Object.keys(updates).length > 0) {
      const { error: updateError } = await supabase
        .from('profiles')
          .update(updates)
        .eq('id', userId)

      if (updateError) throw updateError
      }
    }

    // ✅ Ensure default preferences exist
    await createPreferenceRowsIfMissing(userId)

  } catch (error) {
    console.error('[ensureProfile] Error:', error)
    throw error
  }
}

async function createPreferenceRowsIfMissing(userId: string) {
  // travel_essential_preferences
  const { data: essential } = await supabase
    .from('travel_essential_preferences')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle()

  if (!essential) {
    await supabase.from('travel_essential_preferences').insert({
      user_id: userId,
      preferences: {
        budget: { value: null, weight: 1 },
        travel_type: { value: null, weight: 1 },
        seating: { value: null, weight: 1 },
        climate: { value: null, weight: 1 },
        activities: { value: null, weight: 1 }
      }
    })
  }

  // travel_loyalty_preferences
  const { data: loyalty } = await supabase
    .from('travel_loyalty_preferences')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle()

  if (!loyalty) {
    await supabase.from('travel_loyalty_preferences').insert({
      user_id: userId,
      preferences: {
        airline: { program: null, weight: 1 },
        hotel: { program: null, weight: 1 }
      }
    })
  }

  // travel_preferences (tags array)
  const { data: tags } = await supabase
    .from('travel_preferences')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle()

  if (!tags) {
    await supabase.from('travel_preferences').insert({
      user_id: userId,
      preferences: {
        tags: []
      }
    })
  }
}



export async function getEssentialPreferences(userId: string): Promise<EssentialPreferences | null> {
  try {
    const { data, error } = await supabase
      .from('travel_essential_preferences')
      .select('preferences')
      .eq('user_id', userId)
      .single()

    if (error) {
      if (error.code !== 'PGRST116') {
        console.error('Error loading essential preferences:', error)
      }
      return null
    }

    return data.preferences
  } catch (error) {
    console.error('Error loading essential preferences:', error)
    return null
  }
}

export async function getLoyaltyPreferences(userId: string): Promise<LoyaltyPreferences | null> {
  try {
    const { data, error } = await supabase
      .from('travel_loyalty_preferences')
      .select('preferences')
      .eq('user_id', userId)
      .single()

    if (error) {
      if (error.code !== 'PGRST116') {
        console.error('Error loading loyalty preferences:', error)
      }
      return null
    }

    return data.preferences
  } catch (error) {
    console.error('Error loading loyalty preferences:', error)
    return null
  }
}

export async function saveEssentialPreferences(
  userId: string,
  preferences: EssentialPreferences
): Promise<void> {
  const { error } = await supabase
    .from('travel_essential_preferences')
    .upsert({
      user_id: userId,
      preferences
    }, {
      onConflict: 'user_id'
    })

  if (error) {
    console.error('Failed to save essential preferences:', error)
    throw error
  }
}

export async function saveLoyaltyPreferences(
  userId: string,
  preferences: LoyaltyPreferences
): Promise<void> {
  try {
    // First check if preferences exist
    const { data: existing } = await supabase
      .from('travel_loyalty_preferences')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle()

    if (existing) {
      // Update existing preferences
      const { error: updateError } = await supabase
        .from('travel_loyalty_preferences')
        .update({ preferences })
        .eq('user_id', userId)

      if (updateError) throw updateError
    } else {
      // Insert new preferences
      const { error: insertError } = await supabase
        .from('travel_loyalty_preferences')
        .insert({
          user_id: userId,
          preferences
        })

      if (insertError) throw insertError
    }
  } catch (error) {
    console.error('Failed to save loyalty preferences:', error)
    throw error
  }
}

export async function getTravelPreferences(userId: string): Promise<string[]> {
  try {
    const { data, error } = await supabase
      .from('travel_preferences')
      .select('preferences')
      .eq('user_id', userId)
      .single()

    if (error) {
      if (error.code !== 'PGRST116') {
        console.error('Error loading travel preferences:', error)
      }
      return []
    }

    return data.preferences.tags || []
  } catch (error) {
    console.error('Error loading travel preferences:', error)
    return []
  }
}

export async function saveTravelPreferences(
  userId: string,
  tags: string[]
): Promise<void> {
  const { error } = await supabase
    .from('travel_preferences')
    .upsert({
      user_id: userId,
      preferences: { tags }
    })

  if (error) throw error
}

export async function getUserProfile(): Promise<Profile | null> {
  const { data: { user } } = await supabase.auth.getUser()

  if (!user) return null;

  // Ensure profile exists first
  await ensureProfile(user.id)

  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', user.id)
    .maybeSingle()

  if (error) {
    console.error('Profile fetch error:', error)
    return null
  }

  return data
}

// Enhanced preference functions using preference_type field
export async function getUserPreferences(
  userId: string,
  preferenceType?: PreferenceType
): Promise<UserPreference[]> {
  try {
    let query = supabase
      .from('user_preferences')
      .select('*')
      .eq('user_id', userId)

    if (preferenceType) {
      query = query.eq('preference_type', preferenceType)
    }

    const { data, error } = await query.order('preference_key')

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error loading user preferences:', error)
    return []
  }
}

export async function saveUserPreference(
  userId: string,
  preferenceType: PreferenceType,
  preferenceKey: string,
  value: string | null,
  weight: number = 1
): Promise<void> {
  try {
    const { error } = await supabase
      .from('user_preferences')
      .upsert({
        user_id: userId,
        preference_type: preferenceType,
        preference_key: preferenceKey,
        value,
        weight
      }, {
        onConflict: 'user_id,preference_type,preference_key'
      })

    if (error) throw error
  } catch (error) {
    console.error('Failed to save user preference:', error)
    throw error
  }
}

// Legacy loyalty membership function
export async function updateMembershipPriority(
  userId: string,
  memberships: { id: string; priority: number }[]
): Promise<void> {
  try {
    const updates = memberships.map(m => 
      supabase
        .from('user_loyalty_memberships')
        .update({ priority: m.priority })
        .eq('id', m.id)
        .eq('user_id', userId)
    )

    await Promise.all(updates)
  } catch (error) {
    console.error('Failed to update membership priorities:', error)
    throw error
  }
}

// Quiz functions
export async function saveQuizResults(
  quizResults: Omit<QuizResults, 'id' | 'created_at'>
): Promise<void> {
  try {
    // First, set all existing quiz results for this user/type to inactive
    await supabase
      .from('quiz_results')
      .update({ is_active: false })
      .eq('user_id', quizResults.user_id)
      .eq('quiz_type', quizResults.quiz_type)

    // Then insert the new quiz results
    const { error } = await supabase
      .from('quiz_results')
      .insert(quizResults)

    if (error) throw error

    // VOY-416: refresh Claude traveler intelligence. Fire-and-forget —
    // writes are FE→Supabase direct so the backend doesn't see this insert.
    triggerAgentAnalysis(quizResults.user_id, 'quiz_completed', {
      quiz_type: quizResults.quiz_type,
      quiz_version: quizResults.quiz_version,
    })
  } catch (error) {
    console.error('Failed to save quiz results:', error)
    throw error
  }
}

export async function getActiveQuizResult(
  userId: string,
  quizType?: string
): Promise<QuizResults | null> {
  try {
    let query = supabase
      .from('quiz_results')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)

    if (quizType) {
      query = query.eq('quiz_type', quizType)
    }

    const { data, error } = await query
      .order('completed_at', { ascending: false })
      .limit(1)

    if (error) {
      console.error('[getActiveQuizResult] Supabase error:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint
      })
      return null
    }

    // Return first result or null if empty array
    return data && data.length > 0 ? data[0] : null
  } catch (error) {
    console.error('Error loading quiz results:', error)
    return null
  }
}

// User insights functions
export async function getUserInsights(
  userId: string,
  category?: string
): Promise<UserInsight[]> {
  try {
    let query = supabase
      .from('user_insights')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)

    if (category) {
      query = query.eq('category', category)
    }

    const { data, error } = await query
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error loading user insights:', error)
    return []
  }
}

export async function deleteInsight(
  userId: string,
  insightId: string
): Promise<void> {
  try {
    const { error } = await supabase
      .from('user_insights')
      .update({ is_active: false })
      .eq('id', insightId)
      .eq('user_id', userId)

    if (error) throw error
  } catch (error) {
    console.error('Failed to delete insight:', error)
    throw error
  }
}

// Keywords preferences
export async function getKeywordPreferences(userId: string): Promise<KeywordPreference[]> {
  try {
    const { data, error } = await supabase
      .from('user_preferences')
      .select('*')
      .eq('user_id', userId)
      .eq('preference_type', 'keywords')
      .maybeSingle()

    if (error) {
      if (error.code !== 'PGRST116') { // Not found error
        console.error('Error loading keyword preferences:', error)
      }
      return []
    }

    // Check if keywords exist in preferences JSONB field
    if (data?.preferences?.keywords && Array.isArray(data.preferences.keywords)) {
      return data.preferences.keywords.slice(0, 5).map((k: any) => ({
        keyword: k.text || k.keyword || (typeof k === 'string' ? k : ''),
        weight: k.weight !== undefined ? k.weight : 1
      }))
    }

    return []
  } catch (error) {
    console.error('Error loading keyword preferences:', error)
    return []
  }
}

/**
 * VOY-417 / VOY-438 — read keywords preserving the full enriched shape
 * (text, weight, source, inferred_dimensions, inferred_topics).
 *
 * `getKeywordPreferences` above projects to the legacy {keyword, weight}
 * pair for backward compat with KeywordsCard / keywords-preferences /
 * v485 KeywordsSection (all of which only need rank-ordered display).
 *
 * The You page (VOY-417 §4) needs the `source` field to render
 * provenance-colored pills (chat / quiz / behavior). This function
 * returns the persisted enriched record directly so the cast back to
 * the full shape is honest.
 */
export async function getEnrichedKeywords(userId: string): Promise<EnrichedKeyword[]> {
  try {
    const { data, error } = await supabase
      .from('user_preferences')
      .select('preferences')
      .eq('user_id', userId)
      .eq('preference_type', 'keywords')
      .maybeSingle()

    if (error) {
      if (error.code !== 'PGRST116') {
        console.error('Error loading enriched keywords:', error)
      }
      return []
    }

    const raw = data?.preferences?.keywords
    if (!Array.isArray(raw)) return []

    // Normalize each row through enrichKeyword so we always return the
    // full EnrichedKeyword shape — even on legacy rows that pre-date
    // VOY-438's source/dimensions/topics enrichment. Drop nulls
    // (empty entries) via the type-narrowing filter.
    return raw
      .slice(0, 5)
      .map((k: any) => enrichKeyword({
        text: typeof k === 'string' ? k : (k?.text || k?.keyword || ''),
        weight: typeof k?.weight === 'number' ? k.weight : 1,
        source: k?.source,
        inferred_dimensions: k?.inferred_dimensions,
        inferred_topics: k?.inferred_topics,
      }))
      .filter((k): k is EnrichedKeyword => k !== null)
  } catch (err) {
    console.error('Error loading enriched keywords:', err)
    return []
  }
}

/**
 * VOY-438: static keyword → (dimension, topic) hints. Mirrored verbatim in
 * the backend (`src/api/profile-v2.js`) and the SQL migration
 * (`public.voy_438_keyword_dimensions` / `voy_438_keyword_topics`). All three
 * lists must change together.
 */
export const KEYWORD_DIMENSION_HINTS: Record<string, { dimensions: TravelerDimension[]; topics: string[] }> = {
  beach:      { dimensions: ['urban_vs_nature'], topics: ['coast', 'sun', 'swimming'] },
  city:       { dimensions: ['urban_vs_nature'], topics: ['urban', 'nightlife', 'culture'] },
  cultural:   { dimensions: ['familiar_vs_novel'], topics: ['history', 'museums', 'heritage'] },
  culture:    { dimensions: ['familiar_vs_novel'], topics: ['history', 'museums', 'heritage'] },
  food:       { dimensions: ['food_centrality'], topics: ['cuisine', 'restaurants', 'tasting'] },
  foodie:     { dimensions: ['food_centrality'], topics: ['cuisine', 'restaurants', 'tasting'] },
  adventure:  { dimensions: ['adventure_vs_comfort'], topics: ['outdoor', 'active', 'exploration'] },
  luxury:     { dimensions: ['budget_vs_luxury'], topics: ['premium', 'high-end', 'exclusive'] },
  budget:     { dimensions: ['budget_vs_luxury'], topics: ['affordable', 'value', 'hostels'] },
  family:     { dimensions: ['solo_vs_social'], topics: ['kids', 'multi-generational'] },
  solo:       { dimensions: ['solo_vs_social'], topics: ['independent'] },
  nature:     { dimensions: ['urban_vs_nature'], topics: ['wildlife', 'outdoors', 'scenery'] },
  relaxation: { dimensions: ['adventure_vs_comfort', 'fast_vs_slow'], topics: ['spa', 'quiet', 'retreat'] },
  hiking:     { dimensions: ['urban_vs_nature', 'adventure_vs_comfort'], topics: ['trekking', 'mountains', 'outdoors'] },
  art:        { dimensions: ['familiar_vs_novel'], topics: ['galleries', 'museums', 'design'] },
  nightlife:  { dimensions: ['urban_vs_nature'], topics: ['bars', 'clubs', 'music'] },
  wellness:   { dimensions: ['adventure_vs_comfort'], topics: ['spa', 'yoga', 'retreat'] },
}

/**
 * VOY-438: enrich a raw keyword input with inferred dimensions/topics and a
 * `source` provenance tag. Trims/clamps weight to [1, 5]; returns `null` for
 * empty input so callers can `.filter(Boolean)` away malformed entries.
 *
 * `KeywordSource` and `EnrichedKeyword` are exported from `@/types/profile`
 * — the runtime check below mirrors the backend's `KEYWORD_VALID_SOURCES`
 * whitelist in src/api/profile-v2.js.
 */
const KEYWORD_VALID_SOURCES: KeywordSource[] = ['chat', 'quiz', 'behavior', 'user', 'legacy_migration']
export function enrichKeyword(
  raw: Partial<EnrichedKeyword> & { keyword?: string },
  defaultSource: KeywordSource = 'user'
): EnrichedKeyword | null {
  if (!raw) return null
  const text = (typeof raw.text === 'string' ? raw.text : raw.keyword || '').trim()
  if (!text) return null

  const weightRaw = Number(raw.weight)
  const weight = Number.isFinite(weightRaw)
    ? Math.max(1, Math.min(5, Math.round(weightRaw)))
    : 1
  const source: KeywordSource =
    raw.source && KEYWORD_VALID_SOURCES.includes(raw.source)
      ? raw.source
      : defaultSource

  const hint = KEYWORD_DIMENSION_HINTS[text.toLowerCase()] || { dimensions: [], topics: [] }
  return {
    text,
    weight,
    source,
    inferred_dimensions:
      Array.isArray(raw.inferred_dimensions) && raw.inferred_dimensions.length
        ? raw.inferred_dimensions
        : hint.dimensions,
    inferred_topics:
      Array.isArray(raw.inferred_topics) && raw.inferred_topics.length
        ? raw.inferred_topics
        : hint.topics,
  }
}

export async function saveKeywordPreferences(
  userId: string,
  keywords: KeywordPreference[]
): Promise<void> {
  try {
    // VOY-438: enrich each keyword with inferred_dimensions/inferred_topics
    // and a `source` provenance tag (defaults to 'user' for direct edits via
    // the profile UI). Backend mirrors this enrichment in PUT /keywords.
    const keywordData: EnrichedKeyword[] = keywords
      .map(k => enrichKeyword({ text: k.keyword, weight: k.weight }))
      .filter((k): k is EnrichedKeyword => k !== null)

    // Create the preferences JSONB object
    const preferencesData = {
      keywords: keywordData
    }
    
    console.log('[saveKeywordPreferences] Saving keywords for user:', userId, preferencesData)
    
    // First, check if a row exists
    const { data: existing, error: checkError } = await supabase
      .from('user_preferences')
      .select('id')
      .eq('user_id', userId)
      .eq('preference_type', 'keywords')
      .maybeSingle()
    
    if (checkError && checkError.code !== 'PGRST116') {
      console.error('Error checking existing keywords:', checkError)
      throw checkError
    }
    
    let saveError
    
    if (existing) {
      // Update existing row
      console.log('[saveKeywordPreferences] Updating existing keywords row')
      const { error } = await supabase
        .from('user_preferences')
        .update({
          preferences: preferencesData,
          priority: 0
        })
        .eq('user_id', userId)
        .eq('preference_type', 'keywords')
      
      saveError = error
    } else {
      // Insert new row
      console.log('[saveKeywordPreferences] Inserting new keywords row')
      const { error } = await supabase
        .from('user_preferences')
        .insert({
          user_id: userId,
          preference_type: 'keywords',
          preferences: preferencesData,
          priority: 0
        })
      
      saveError = error
    }
    
    if (saveError) {
      console.error('Failed to save keyword preferences:', saveError)
      throw saveError
    }

    console.log('[saveKeywordPreferences] Keywords saved successfully')

    // VOY-416: user edited their stated keyword preferences — debounced
    // agent refresh. Covers both insert (first save) and update (edits or
    // full removal when keywords=[]).
    triggerAgentAnalysis(userId, 'preference_changed', {
      type: 'keywords_save',
      count: keywords.length,
    })
  } catch (error) {
    console.error('Failed to save keyword preferences:', error)
    throw error
  }
}

// Profile update function
export async function updateProfile(
  userId: string,
  updates: Partial<Profile>
): Promise<void> {
  try {
    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)

    if (error) throw error
  } catch (error) {
    console.error('Failed to update profile:', error)
    throw error
  }
}

// Calculate profile completeness with detailed breakdown
export function calculateProfileCompleteness(profile: Profile): number {
  const weights = {
    basicInfo: 40,
    travelPreferences: 20,
    loyaltyPrograms: 15,
    quiz: 20,
    keywords: 5
  }

  let totalScore = 0

  // Basic Info: 40% (name, last_name, phone, bio, birth_date)
  const basicFields = ['name', 'last_name', 'phone', 'bio', 'birth_date']
  const completedBasicFields = basicFields.filter(field => {
    const value = profile[field as keyof Profile]
    return value !== null && value !== undefined && value !== ''
  }).length
  const basicScore = (completedBasicFields / basicFields.length) * weights.basicInfo
  totalScore += basicScore

  // For other components, we'll need to fetch additional data
  // This is a simplified version - in reality, you'd check these from the database
  // For now, we'll just check if they exist
  
  return Math.round(totalScore)
}

// Enhanced profile completeness calculation with detailed breakdown.
//
// VOY-417 review #4 (perf): accepts an optional pre-loaded `profile` so
// callers that already fetched it (ProfilePage, YouPage, anywhere using
// `getCompleteProfile`) don't double-fetch. The internal `getUserProfile()`
// runs only when the caller didn't supply one.
export async function getProfileCompleteness(
  userId: string,
  profileOverride?: Profile | null,
): Promise<ProfileCompleteness> {
  try {
    console.log('[VOY-93] Calculating profile completeness for user:', userId)

    // Use the caller-supplied profile when present; only fetch when absent.
    const profile = profileOverride !== undefined ? profileOverride : await getUserProfile()
    if (!profile) {
      console.log('[VOY-93] No profile found')
      return {
        overall: 0,
        breakdown: {
          basicInfo: { score: 0, max: 40, complete: false },
          travelPreferences: { score: 0, max: 20, complete: false },
          loyaltyPrograms: { score: 0, max: 15, complete: false },
          quiz: { score: 0, max: 20, complete: false },
          keywords: { score: 0, max: 5, complete: false }
        }
      }
    }

    const breakdown = {
      basicInfo: { score: 0, max: 40, complete: false },
      travelPreferences: { score: 0, max: 20, complete: false },
      loyaltyPrograms: { score: 0, max: 15, complete: false },
      quiz: { score: 0, max: 20, complete: false },
      keywords: { score: 0, max: 5, complete: false }
    }

    // Calculate Basic Info (40%)
    const basicFields = ['name', 'last_name', 'phone', 'bio', 'birth_date']
    const completedBasicFields = basicFields.filter(field => {
      const value = profile[field as keyof Profile]
      return value !== null && value !== undefined && value !== ''
    }).length
    breakdown.basicInfo.score = Math.round((completedBasicFields / basicFields.length) * breakdown.basicInfo.max)
    breakdown.basicInfo.complete = completedBasicFields === basicFields.length
    console.log('[VOY-93] Basic Info:', completedBasicFields, '/', basicFields.length, '=', breakdown.basicInfo.score, 'points')

    // Check Travel Preferences (20%)
    // Check user_preferences with type='travel' (from PreferencesTab)
    const travelPrefs = await getPreferences(userId, 'travel')
    const travelPrefsCount = Object.keys(travelPrefs || {}).filter(key => {
      const value = travelPrefs[key]
      // Count fields that have non-empty values
      if (Array.isArray(value)) return value.length > 0
      return value !== null && value !== undefined && value !== ''
    }).length
    
    // Consider complete if at least 4 preference fields are filled
    if (travelPrefsCount >= 4) {
      breakdown.travelPreferences.score = breakdown.travelPreferences.max
      breakdown.travelPreferences.complete = true
    } else if (travelPrefsCount > 0) {
      breakdown.travelPreferences.score = Math.round((travelPrefsCount / 4) * breakdown.travelPreferences.max)
    }
    console.log('[VOY-93] Travel Preferences:', travelPrefsCount, 'fields =', breakdown.travelPreferences.score, 'points')

    // Check Loyalty Programs (15%)
    const loyaltyMemberships = await getUserLoyaltyMemberships(userId)
    console.log('[VOY-93] Loyalty Memberships found:', loyaltyMemberships?.length || 0)
    if (loyaltyMemberships && loyaltyMemberships.length > 0) {
      // Score: 7.5 points per program, max 15 points
      breakdown.loyaltyPrograms.score = Math.min(loyaltyMemberships.length * 7.5, breakdown.loyaltyPrograms.max)
      breakdown.loyaltyPrograms.complete = loyaltyMemberships.length >= 2 // VOY-93: Changed from 3 to 2
    }
    console.log('[VOY-93] Loyalty Programs:', loyaltyMemberships?.length || 0, 'programs =', breakdown.loyaltyPrograms.score, 'points')

    // Check Quiz Completion (20%)
    let quizResult = await getUserQuizResults(userId)
    
    // Fallback to localStorage if not in Supabase (should rarely happen now)
    if (!quizResult) {
      try {
        const cachedData = localStorage.getItem(`quiz_result_${userId}`)
        if (cachedData) {
          const parsed = JSON.parse(cachedData)
          quizResult = parsed.result
        }
      } catch (error) {
        // Silent fail - localStorage is just a fallback
      }
    }
    
    console.log('[VOY-93] Quiz Result found:', quizResult ? 'YES' : 'NO')
    if (quizResult) {
      breakdown.quiz.score = breakdown.quiz.max
      breakdown.quiz.complete = true
    }
    console.log('[VOY-93] Quiz:', breakdown.quiz.complete ? 'Complete' : 'Incomplete', '=', breakdown.quiz.score, 'points')

    // Check Keywords (5%)
    const keywords = await getKeywordPreferences(userId)
    console.log('[VOY-93] Keywords found:', keywords?.length || 0)
    if (keywords && keywords.length >= 3) {
      breakdown.keywords.score = breakdown.keywords.max
      breakdown.keywords.complete = true
    } else if (keywords && keywords.length > 0) {
      breakdown.keywords.score = Math.round((keywords.length / 3) * breakdown.keywords.max)
    }
    console.log('[VOY-93] Keywords:', keywords?.length || 0, 'keywords =', breakdown.keywords.score, 'points')

    const overall = Object.values(breakdown).reduce((sum, item) => sum + item.score, 0)
    console.log('[VOY-93] ✅ TOTAL COMPLETENESS:', overall, '%')
    console.log('[VOY-93] Breakdown:', JSON.stringify(breakdown, null, 2))

    return {
      overall,
      breakdown
    }
  } catch (error) {
    console.error('Error calculating profile completeness:', error)
    return {
      overall: 0,
      breakdown: {
        basicInfo: { score: 0, max: 40, complete: false },
        travelPreferences: { score: 0, max: 20, complete: false },
        loyaltyPrograms: { score: 0, max: 15, complete: false },
        quiz: { score: 0, max: 20, complete: false },
        keywords: { score: 0, max: 5, complete: false }
      }
    }
  }
}

// Get complete profile with all preferences
export async function getCompleteProfile(userId: string): Promise<{
  profile: Profile | null
  preferences: {
    travel: UserPreference[]
    keywords: KeywordPreference[]
    loyalty: UserLoyaltyMembership[]
  }
  completeness: ProfileCompleteness
  insights: UserInsight[]
}> {
  try {
    // VOY-417 review #4: fetch profile first, then thread it into
    // getProfileCompleteness so we don't double-fetch. The rest of the
    // slices stay in Promise.all alongside completeness (profile is
    // already resolved by the time we hit this line).
    const profile = await getUserProfile()
    const [travelPrefs, keywords, loyalty, completeness, insights] = await Promise.all([
      getUserPreferences(userId, 'travel'),
      getKeywordPreferences(userId),
      getUserLoyaltyMemberships(userId),
      getProfileCompleteness(userId, profile),
      getUserInsights(userId)
    ])

    return {
      profile,
      preferences: {
        travel: travelPrefs,
        keywords,
        loyalty
      },
      completeness,
      insights
    }
  } catch (error) {
    console.error('Error getting complete profile:', error)
    return {
      profile: null,
      preferences: {
        travel: [],
        keywords: [],
        loyalty: []
      },
      completeness: {
        overall: 0,
        breakdown: {
          basicInfo: { score: 0, max: 40, complete: false },
          travelPreferences: { score: 0, max: 20, complete: false },
          loyaltyPrograms: { score: 0, max: 15, complete: false },
          quiz: { score: 0, max: 20, complete: false },
          keywords: { score: 0, max: 5, complete: false }
        }
      },
      insights: []
    }
  }
}

// Save preferences with proper type handling
export async function savePreferences(
  userId: string,
  type: PreferenceType,
  preferences: Record<string, any>,
  priority: number = 1
): Promise<void> {
  try {
    console.log('Saving preferences:', { userId, type, preferences })

    // Clean up preferences - remove undefined/null values
    const cleanedPreferences = Object.entries(preferences)
      .filter(([_, value]) => {
        if (value === undefined || value === null) return false
        if (typeof value === 'string' && value.trim() === '') return false
        return true
      })
      .reduce((acc, [key, value]) => {
        acc[key] = value
        return acc
      }, {} as Record<string, any>)

    console.log('Cleaned preferences:', cleanedPreferences)

    // VOY-352: pick the most recent row when duplicates exist. Historically
    // the table had no unique constraint on (user_id, preference_type), so
    // any call to .maybeSingle() would throw when the user accumulated
    // duplicate rows — which then fell through to a blind INSERT and created
    // yet another duplicate. This picks the latest row idempotently.
    const { data: existingRows } = await supabase
      .from('user_preferences')
      .select('id')
      .eq('user_id', userId)
      .eq('preference_type', type)
      .order('updated_at', { ascending: false })
      .limit(1)
    const existing = existingRows && existingRows.length > 0 ? existingRows[0] : null

    if (existing) {
      // Update existing row — target by primary key so we touch exactly one
      // row even if stale duplicates still live in the table.
      console.log('Updating existing preferences')
      const { error } = await supabase
        .from('user_preferences')
        .update({
          preferences: cleanedPreferences,
          priority: priority,
          updated_at: new Date().toISOString(),
        })
        .eq('id', existing.id)

      if (error) {
        console.error('Failed to update preferences. Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        })
        throw new Error(`Database error: ${error.message}`)
      }
    } else {
      // Insert new row
      console.log('Inserting new preferences')
      const { error } = await supabase
        .from('user_preferences')
        .insert({
          user_id: userId,
          preference_type: type,
          preferences: cleanedPreferences,
          priority: priority
        })

      if (error) {
        console.error('Failed to insert preferences. Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        })
        throw new Error(`Database error: ${error.message}`)
      }
    }

    console.log('Successfully saved preferences')
  } catch (error) {
    console.error('Failed to save preferences:', error)
    throw error
  }
}

// Get preferences with proper type handling
export async function getPreferences(
  userId: string,
  type: PreferenceType
): Promise<Record<string, any>> {
  try {
    // VOY-352: tolerate duplicate rows — legacy bug in savePreferences
    // left many (user_id, preference_type) duplicates in the table and
    // .maybeSingle() throws on >1 match. Order by updated_at DESC and
    // pick the most recent row, which is always the latest successful save.
    const { data, error } = await supabase
      .from('user_preferences')
      .select('*')
      .eq('user_id', userId)
      .eq('preference_type', type)
      .order('updated_at', { ascending: false })
      .limit(1)

    if (error) {
      console.error('Error getting preferences:', error)
      return {}
    }

    const row = data && data.length > 0 ? data[0] : null
    if (!row) {
      console.log('No preferences found for user')
      return {}
    }

    // Try different possible column names for the JSON data
    const jsonData = row.data || row.preferences || row.value

    if (jsonData) {
      try {
        return typeof jsonData === 'string'
          ? JSON.parse(jsonData)
          : jsonData
      } catch {
        return jsonData || {}
      }
    }

    // Return the whole row as preferences (fallback)
    return row
  } catch (error) {
    console.error('Error getting preferences:', error)
    return {}
  }
}

// ============= NEW LOYALTY PROGRAM FUNCTIONS =============

// Get all loyalty programs from catalog
export async function getLoyaltyPrograms(
  type?: 'airline' | 'hotel' | 'car_rental' | 'other'
): Promise<LoyaltyProgram[]> {
  try {
    let query = supabase
      .from('loyalty_programs_catalog')
      .select('*')
      .eq('is_active', true)
    
    if (type) {
      query = query.eq('program_type', type)
    }
    
    const { data, error } = await query.order('program_name')
    
    if (error) throw error
    
    // Transform the data to match LoyaltyProgram interface
    return (data || []).map(item => ({
      id: item.id,
      program_type: item.program_type || item.type,
      program_name: item.program_name,
      program_code: item.program_code || item.program_name.toLowerCase().replace(/\s+/g, '_'),
      company: item.company,
      tiers: item.tiers || item.tier_names || [],
      metadata: {
        logo_url: item.logo_url,
        website_url: item.website_url,
        brand_color: item.brand_color,
        benefits_summary: item.benefits_summary
      },
      is_active: item.is_active,
      created_at: item.created_at,
      updated_at: item.updated_at
    }))
  } catch (error) {
    console.error('Error fetching loyalty programs:', error)
    return []
  }
}

// Legacy function for backward compatibility
export async function getLoyaltyProgramsCatalog(
  type?: 'airline' | 'hotel' | 'car_rental' | 'credit_card' | 'other'
): Promise<LoyaltyProgramCatalog[]> {
  // Filter out credit_card type as it's not supported in the new function
  const supportedType = type === 'credit_card' ? 'other' : type
  return getLoyaltyPrograms(supportedType) as any
}

// Get user's loyalty memberships with program details
export async function getUserLoyaltyMemberships(
  userId: string
): Promise<UserLoyaltyMembership[]> {
  try {
    const { data, error } = await supabase
      .from('user_loyalty_memberships')
      .select(`
        *,
        program:loyalty_programs_catalog(*)
      `)
      .eq('user_id', userId)
      .eq('is_active', true)
      .order('priority')
    
    if (error) throw error
    
    // Transform the data to match UserLoyaltyMembership interface
    return (data || []).map(item => ({
      id: item.id,
      user_id: item.user_id,
      program_id: item.program_id,
      member_id: item.member_id || '',
      tier: item.tier,
      expiration_date: item.expiration_date,
      priority: item.priority || 999,
      points_balance: item.points_balance,
      is_primary: item.is_primary || false,
      is_active: item.is_active,
      created_at: item.created_at,
      updated_at: item.updated_at,
      program: item.program ? {
        id: item.program.id,
        program_type: item.program.program_type || item.program.type,
        program_name: item.program.program_name,
        program_code: item.program.program_code || item.program.program_name.toLowerCase().replace(/\s+/g, '_'),
        company: item.program.company,
        tiers: item.program.tiers || item.program.tier_names || [],
        metadata: {
          logo_url: item.program.logo_url,
          website_url: item.program.website_url,
          brand_color: item.program.brand_color,
          benefits_summary: item.program.benefits_summary
        },
        is_active: item.program.is_active
      } : undefined
    }))
  } catch (error) {
    console.error('Error fetching user loyalty memberships:', error)
    return []
  }
}

// Save or update a loyalty membership
export async function saveLoyaltyMembership(
  membership: Partial<UserLoyaltyMembership>
): Promise<void> {
  try {
    const { error } = await supabase
      .from('user_loyalty_memberships')
      .upsert({
        ...membership,
        updated_at: new Date().toISOString()
      }, { 
        onConflict: 'user_id,program_id'
      })
    
    if (error) throw error
  } catch (error) {
    console.error('Error saving loyalty membership:', error)
    throw error
  }
}

// Delete a loyalty membership (soft delete)
export async function deleteLoyaltyMembership(
  membershipId: string
): Promise<void> {
  try {
    const { error } = await supabase
      .from('user_loyalty_memberships')
      .update({ 
        is_active: false,
        updated_at: new Date().toISOString()
      })
      .eq('id', membershipId)
    
    if (error) throw error
  } catch (error) {
    console.error('Error deleting loyalty membership:', error)
    throw error
  }
}

// Reorder loyalty memberships by updating priorities
export async function reorderLoyaltyMemberships(
  memberships: { id: string; priority: number }[]
): Promise<void> {
  try {
    // Update all priorities in parallel
    const updates = memberships.map(m => 
      supabase
        .from('user_loyalty_memberships')
        .update({ 
          priority: m.priority,
          updated_at: new Date().toISOString()
        })
        .eq('id', m.id)
    )
    
    const results = await Promise.all(updates)
    
    // Check if any updates failed
    const errors = results.filter(r => r.error)
    if (errors.length > 0) {
      throw new Error('Failed to update some membership priorities')
    }
  } catch (error) {
    console.error('Error reordering loyalty memberships:', error)
    throw error
  }
}

// Add a new loyalty membership
export async function addLoyaltyMembership(
  userId: string,
  programId: string,
  memberData?: {
    member_id?: string;
    tier?: string;
    expiration_date?: string;
  }
): Promise<void> {
  try {
    // Get the highest priority
    const { data: existingMemberships } = await supabase
      .from('user_loyalty_memberships')
      .select('priority')
      .eq('user_id', userId)
      .eq('is_active', true)
      .order('priority', { ascending: false })
      .limit(1)
    
    const nextPriority = existingMemberships && existingMemberships.length > 0 
      ? (existingMemberships[0].priority || 0) + 1 
      : 1
    
    const { error } = await supabase
      .from('user_loyalty_memberships')
      .insert({
        user_id: userId,
        program_id: programId,
        member_id: memberData?.member_id || '',
        tier: memberData?.tier,
        expiration_date: memberData?.expiration_date,
        priority: nextPriority,
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
    
    if (error) throw error
  } catch (error) {
    console.error('Error adding loyalty membership:', error)
    throw error
  }
}

// Quiz-related functions

export async function getQuizConfig() {
  try {
    const { data, error } = await supabase
      .from('quiz_config')
      .select('*')
      .eq('is_active', true)
      .single()
    
    if (error) {
      console.error('Error fetching quiz config:', error)
      return null
    }
    
    return data
  } catch (error) {
    console.error('Error in getQuizConfig:', error)
    return null
  }
}

export async function getUserQuizResults(userId: string) {
  try {
    const { data, error } = await supabase
      .from('quiz_results')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)
      .order('completed_at', { ascending: false })
      .limit(1)
      .maybeSingle()
    
    if (error) {
      console.error('Error fetching quiz results:', error)
      return null
    }
    
    return data
  } catch (error) {
    console.error('Error in getUserQuizResults:', error)
    return null
  }
}

// VOY-414: legacy v1 answer (category + weight) and v2 answer (dimensions) both
// flow through submitQuiz. v2 answers include the full option so we can score
// without re-reading the quiz config server-side.
interface QuizAnswerV1 {
  question_id: string
  option_id: string
  category?: string
  weight?: number
}

interface QuizAnswerV2 {
  question_id: string
  option_id: string
  question_weight: number
  dimensions: Partial<Record<TravelerDimension, number>>
  metadata_capture?: string // e.g. "trip_frequency"
  metadata_value?: string // option text captured as metadata
}

type QuizAnswerAny = QuizAnswerV1 | QuizAnswerV2

// PR#263 review: `typeof null === 'object'` would otherwise let a null
// `dimensions` pass this guard and blow up inside `Object.entries` downstream.
function isV2Answer(a: QuizAnswerAny): a is QuizAnswerV2 {
  return typeof (a as QuizAnswerV2).question_weight === 'number'
    && (a as QuizAnswerV2).dimensions !== null
    && typeof (a as QuizAnswerV2).dimensions === 'object'
}

const ALL_DIMENSIONS: TravelerDimension[] = [
  'adventure_vs_comfort',
  'planned_vs_spontaneous',
  'budget_vs_luxury',
  'solo_vs_social',
  'fast_vs_slow',
  'familiar_vs_novel',
  'urban_vs_nature',
  'food_centrality',
]

function emptyVector(): TravelerVector {
  return ALL_DIMENSIONS.reduce((acc, d) => {
    acc[d] = 0
    return acc
  }, {} as TravelerVector)
}

// Clamp to [-1, 1] for most dimensions, [0, 1] for food_centrality.
function clampDimension(dim: TravelerDimension, v: number): number {
  if (dim === 'food_centrality') return Math.max(0, Math.min(1, v))
  return Math.max(-1, Math.min(1, v))
}

// VOY-414: weighted-average scoring. For each dimension, sum (value * weight)
// across answers that touched that dimension, then divide by total weight
// applied to that dimension. Confidence per dimension is the applied weight
// divided by the theoretical maximum (all relevant questions answered at max
// weight) — gives meaningful confidence when branching skips questions.
export function calculateTravelerVector(
  answers: QuizAnswerV2[],
  allQuestions: QuizQuestionV2[]
): TravelerProfile {
  const numer = emptyVector()
  const denom = emptyVector()
  for (const answer of answers) {
    const w = answer.question_weight || 1
    for (const [dim, value] of Object.entries(answer.dimensions)) {
      if ((ALL_DIMENSIONS as string[]).includes(dim) && typeof value === 'number') {
        const d = dim as TravelerDimension
        numer[d] += value * w
        denom[d] += w
      }
    }
  }

  const vector = emptyVector()
  for (const d of ALL_DIMENSIONS) {
    vector[d] = denom[d] > 0 ? clampDimension(d, numer[d] / denom[d]) : 0
  }

  // Theoretical max weight per dimension = sum of weights of all quiz questions
  // whose option set touches that dimension. Any question with at least one
  // option that assigns this dimension counts.
  const maxWeights = emptyVector()
  for (const q of allQuestions) {
    const touched = new Set<TravelerDimension>()
    for (const opt of q.options) {
      for (const dim of Object.keys(opt.dimensions || {})) {
        if ((ALL_DIMENSIONS as string[]).includes(dim)) {
          touched.add(dim as TravelerDimension)
        }
      }
    }
    for (const d of touched) {
      maxWeights[d] += q.weight || 1
    }
  }

  const confidence = emptyVector()
  for (const d of ALL_DIMENSIONS) {
    confidence[d] = maxWeights[d] > 0 ? Math.min(1, denom[d] / maxWeights[d]) : 0
  }

  const values = ALL_DIMENSIONS.map(d => confidence[d])
  const overall = values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0

  const { primary_label, secondary_traits } = labelsFromVector(vector, confidence)

  return {
    vector,
    confidence_per_dimension: confidence,
    overall_confidence: overall,
    primary_label,
    secondary_traits,
    narrative: narrativeFromVector(vector),
  }
}

// Human-readable labels derived from dominant dimensions. Primary is the
// dimension with the largest absolute value; secondaries are up to 2 more
// dimensions with |value| >= 0.4.
// PR#263 review: takes confidence so a high-magnitude but zero-confidence dim
// (e.g. if the vector is ever assembled externally) can't produce a bogus
// label. Internal callers guarantee value=0 when confidence=0, so the filter
// is a belt-and-braces guard.
function labelsFromVector(
  vector: TravelerVector,
  confidence?: TravelerVector,
): {
  primary_label: string
  secondary_traits: string[]
} {
  const minConf = 0.1
  const byMag = ALL_DIMENSIONS
    .map(d => ({ dim: d, value: vector[d], conf: confidence?.[d] ?? 1 }))
    .filter(({ conf }) => conf >= minConf)
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))

  const primary_label = byMag[0] ? dimensionLabel(byMag[0].dim, byMag[0].value) : 'Balanced Traveler'
  const secondary_traits = byMag
    .slice(1)
    .filter(({ value }) => Math.abs(value) >= 0.4)
    .slice(0, 2)
    .map(({ dim, value }) => dimensionLabel(dim, value))

  return { primary_label, secondary_traits }
}

function dimensionLabel(dim: TravelerDimension, value: number): string {
  const labels: Record<TravelerDimension, [string, string]> = {
    adventure_vs_comfort: ['Comfort Seeker', 'Adventure Seeker'],
    planned_vs_spontaneous: ['Meticulous Planner', 'Spontaneous Wanderer'],
    budget_vs_luxury: ['Budget Optimizer', 'Luxury Traveler'],
    solo_vs_social: ['Group Traveler', 'Independent Explorer'],
    fast_vs_slow: ['Deep Diver', 'Fast Explorer'],
    familiar_vs_novel: ['Familiar Favorites', 'Novelty Seeker'],
    urban_vs_nature: ['Nature Lover', 'City Explorer'],
    food_centrality: ['Casual Diner', 'Culinary Connoisseur'],
  }
  const [low, high] = labels[dim]
  // food_centrality is [0,1]; mid-point 0.5
  const threshold = dim === 'food_centrality' ? 0.5 : 0
  return value >= threshold ? high : low
}

function narrativeFromVector(vector: TravelerVector): string {
  const traits: string[] = []
  if (vector.adventure_vs_comfort > 0.4) traits.push('thrives on adventure')
  else if (vector.adventure_vs_comfort < -0.4) traits.push('values comfort and familiarity')
  if (vector.budget_vs_luxury > 0.4) traits.push('seeks premium experiences')
  else if (vector.budget_vs_luxury < -0.4) traits.push('travels resourcefully')
  if (vector.planned_vs_spontaneous > 0.4) traits.push('embraces spontaneity')
  else if (vector.planned_vs_spontaneous < -0.4) traits.push('plans in detail')
  if (vector.food_centrality > 0.6) traits.push('plans around food')
  if (traits.length === 0) return 'A balanced traveler who adapts to each trip.'
  return `A traveler who ${traits.join(', ')}.`
}

// Project the 8-dim vector into legacy category_scores (0–100) so the existing
// TravelerDnaCard fallback still renders something coherent for old readers.
function vectorToCategoryScores(vector: TravelerVector): Record<string, number> {
  const scaled = (v: number) => Math.round(((v + 1) / 2) * 100)
  const foodScaled = Math.round(vector.food_centrality * 100)
  return {
    adventure: scaled(vector.adventure_vs_comfort),
    luxury: scaled(vector.budget_vs_luxury),
    spontaneous: scaled(vector.planned_vs_spontaneous),
    solo: scaled(vector.solo_vs_social),
    fast_pace: scaled(vector.fast_vs_slow),
    novelty: scaled(vector.familiar_vs_novel),
    urban: scaled(vector.urban_vs_nature),
    foodie: foodScaled,
  }
}

export async function submitQuiz(
  userId: string,
  answers: QuizAnswerAny[],
  allQuestions?: QuizQuestionV2[]
) {
  try {
    // V2 path: dimensions-based answers → compute traveler vector + derive
    // legacy-compat fields so old readers keep working.
    const v2Answers = answers.filter(isV2Answer) as QuizAnswerV2[]
    const useV2 = v2Answers.length > 0 && Array.isArray(allQuestions)

    let quizResultData: Record<string, unknown>

    if (useV2) {
      const profile = calculateTravelerVector(v2Answers, allQuestions!)
      const categoryScores = vectorToCategoryScores(profile.vector)

      // Capture metadata fields (e.g. trip_frequency) from differentiator
      // questions into a flat object for the agent layer.
      const metadata: Record<string, string> = {}
      for (const ans of v2Answers) {
        if (ans.metadata_capture && ans.metadata_value) {
          metadata[ans.metadata_capture] = ans.metadata_value
        }
      }

      const personalityMatrix = {
        primary_category: profile.primary_label,
        secondary_category: profile.secondary_traits?.[0] ?? null,
        confidence_score: profile.overall_confidence,
        category_scores: categoryScores,
        profile_description: profile.narrative,
      }

      quizResultData = {
        id: crypto.randomUUID(),
        user_id: userId,
        quiz_version: '2.0',
        raw_answers: answers,
        profile_categories: [profile.primary_label, ...(profile.secondary_traits ?? [])].filter(Boolean),
        personality_matrix: personalityMatrix,
        confidence_score: profile.overall_confidence,
        traveler_vector: profile,
        metadata,
        is_active: true,
        completed_at: new Date().toISOString(),
      }
    } else {
      // Legacy v1 path — unchanged scoring.
      const v1Answers = answers as QuizAnswerV1[]
      const categoryScores: Record<string, number> = {}
      let totalWeight = 0
      v1Answers.forEach(answer => {
        if (answer.category) {
          const weight = answer.weight || 1
          categoryScores[answer.category] = (categoryScores[answer.category] || 0) + weight
          totalWeight += weight
        }
      })
      const normalizedScores: Record<string, number> = {}
      Object.entries(categoryScores).forEach(([category, score]) => {
        normalizedScores[category] = (score / totalWeight) * 100
      })
      const sortedCategories = Object.entries(normalizedScores).sort(([, a], [, b]) => b - a)
      const primaryCategory = sortedCategories[0]?.[0] || 'Balanced'
      const secondaryCategory = sortedCategories[1]?.[0] || null
      const confidenceScore = Math.min(1, Math.max(0, (sortedCategories[0]?.[1] || 0) / 100))
      const profileDescription = generateProfileDescription(primaryCategory, secondaryCategory)
      const personalityMatrix = {
        primary_category: formatCategoryName(primaryCategory),
        secondary_category: secondaryCategory ? formatCategoryName(secondaryCategory) : null,
        confidence_score: confidenceScore,
        category_scores: normalizedScores,
        profile_description: profileDescription,
      }

      quizResultData = {
        id: crypto.randomUUID(),
        user_id: userId,
        quiz_version: '1.0',
        raw_answers: answers,
        profile_categories: [primaryCategory, secondaryCategory].filter(Boolean),
        personality_matrix: personalityMatrix,
        confidence_score: confidenceScore,
        is_active: true,
        completed_at: new Date().toISOString(),
      }
    }

    try {
      console.log('[submitQuiz] Attempting to save to Supabase...')
      const { data, error } = await supabase
        .from('quiz_results')
        .insert(quizResultData)
        .select()
        .single()

      if (error) {
        console.error('[submitQuiz] Supabase save failed:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
        })
        console.warn('[submitQuiz] ⚠️ Quiz will only be saved to localStorage')
        return quizResultData
      }

      console.log('[submitQuiz] ✅ Successfully saved to Supabase')

      // VOY-416: refresh Claude traveler intelligence (FE→Supabase direct).
      triggerAgentAnalysis(userId, 'quiz_completed', {
        quiz_type: 'travel_personality',
        quiz_version: '2.0',
      })

      return data
    } catch (dbError) {
      console.error('[submitQuiz] Database error:', dbError)
      console.warn('[submitQuiz] ⚠️ Falling back to localStorage only')
      return quizResultData
    }
  } catch (error) {
    console.error('Error submitting quiz:', error)
    throw error
  }
}

/**
 * VOY-415: fetch the unified traveler context from the backend. Uses apiFetch
 * so the Supabase session token is attached automatically and ensureSelf on
 * the backend passes.
 *
 * PR#263 review: returns null only when the backend explicitly says "no
 * profile" (404). Authentication / authorization failures (401, 403) and
 * server errors (5xx) throw — silently swallowing them would hide real bugs
 * from any consumer (itinerary gen, NBs framework, etc.).
 */
export class AgentContextError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message)
    this.name = 'AgentContextError'
  }
}

export async function getAgentContext(userId: string): Promise<AgentContext | null> {
  const res = await apiFetch(`/api/v2/profile/${userId}/agent-context`)
  if (res.ok) {
    return (await res.json()) as AgentContext
  }
  if (res.status === 404) {
    // No profile row — legitimate "no data" state, callers can render an
    // empty view instead of failing.
    return null
  }
  const body = await res.json().catch(() => ({}))
  throw new AgentContextError(
    body?.error || `agent-context request failed (${res.status})`,
    res.status,
  )
}

/**
 * VOY-503: fetch the multi-dimensional traveler analysis emitted by the
 * profile-intelligence agent.
 *
 * Returns `null` when there is no agent_profile row yet (a brand-new user
 * before the first triggerAnalysis fires). All other failures throw — the
 * You page consumer should render an empty state for `null` and an error
 * state for thrown errors, never silently swallow.
 *
 * Throws `AgentContextError` (same shape as `getAgentContext`) so VOY-417
 * can share a single error-handling code path for both fetchers — `.status`
 * is available to distinguish 401/403/5xx.
 *
 * The shape mirrors `user_agent_profile`. Consumers that only care about
 * the legacy VOY-416 fields (`narrative_summary`, `behavioral_signals`,
 * `confidence_overall`) keep working unchanged; the VOY-503 additions
 * (`archetype`, `style_attributes`, `memory_feed`, `recommendations`) are
 * the contract with the VOY-417 You page redesign.
 */
export async function getAgentProfile(userId: string): Promise<AgentProfile | null> {
  const res = await apiFetch(`/api/agent/profile/${userId}`)
  if (res.ok) {
    // `apiFetch`'s GET on this endpoint returns either `null` (no row) or
    // the JSON-encoded row. Pass through directly — Express `res.json(null)`
    // serialises to `"null"`, which parses back to `null`.
    return (await res.json()) as AgentProfile | null
  }
  if (res.status === 404) return null
  const body = await res.json().catch(() => ({}))
  throw new AgentContextError(
    body?.error || `agent profile request failed (${res.status})`,
    res.status,
  )
}

// =============================================================================
// VOY-458: Memory Editor — backend-only ticket. These helpers are typed
// stubs for VOY-417 to call when the UI ships. Each hits one of the four
// POST endpoints on /api/v2/profile/:userId/memory/* and returns the
// updated override row (or { deleted: true } for delete). Failures throw
// AgentContextError for consistent error handling with getAgentContext /
// getAgentProfile.
// =============================================================================

export interface MemoryOverride {
  id: string
  user_id: string
  dimension: string
  value: unknown
  pinned: boolean
  confidence: number
  /**
   * Raw DB column name. Maps to `last_confirmed_at` on the
   * `ResolvedPreference` API projection — the resolver renames it.
   * Code that bridges between the two shapes must rename the field.
   */
  confirmed_at: string | null
  confirmed_count: number
  source_event: string | null
  reason: string | null
  created_at: string
  updated_at: string
}

async function postMemoryAction<T>(
  userId: string,
  action: MemoryAction,
  body: Record<string, unknown>,
): Promise<T> {
  const res = await apiFetch(`/api/v2/profile/${userId}/memory/${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (res.ok) return (await res.json()) as T
  const errBody = await res.json().catch(() => ({}))
  throw new AgentContextError(
    errBody?.error || `memory ${action} failed (${res.status})`,
    res.status,
  )
}

/**
 * VOY-458: endorse the current resolved value of a dimension. Sets
 * confidence=1.0, pinned=false (user endorses but does not lock — AI may
 * still re-derive), increments confirmed_count, sets confirmed_at=now().
 *
 * `value` is optional: when omitted, the backend reads the existing
 * override value (or null for first-time confirm of a resolver-only value).
 */
export async function confirmMemoryFact(
  userId: string,
  dimension: ProfileDimension | string,
  value?: unknown,
): Promise<{ success: boolean; override: MemoryOverride }> {
  return postMemoryAction(userId, 'confirm', { dimension, value })
}

/**
 * VOY-458: replace the current value with `new_value`, locked (pinned=true,
 * confidence=1.0). The prior value is tombstoned in `user_corrections`
 * so the agent will not re-derive it.
 *
 * Also resets `confirmed_count` to 0 and `confirmed_at` to null — the
 * prior confirmations applied to the OLD value.
 *
 * `prior_value` lets the client pass the value being rejected explicitly
 * (e.g. when the user is correcting an AI-derived value that is not yet
 * in `user_preference_overrides`). When omitted, the backend reads the
 * prior override value.
 */
export async function correctMemoryFact(
  userId: string,
  dimension: ProfileDimension | string,
  newValue: unknown,
  opts: { reason?: string; priorValue?: unknown } = {},
): Promise<{ success: boolean; override: MemoryOverride }> {
  return postMemoryAction(userId, 'correct', {
    dimension,
    new_value: newValue,
    reason: opts.reason,
    prior_value: opts.priorValue,
  })
}

/**
 * VOY-458: lock the current value (pinned=true) without changing it. Used
 * when the resolved value is correct but the user wants to freeze it
 * against future AI revisions.
 *
 * **`value` is required at the backend when no prior override row exists**
 * (e.g. first-time pin of a resolver-derived value). The TS signature
 * marks it optional because the common case is pinning an existing value,
 * but the backend will return **400 Bad Request** with
 * `"value is required when no prior override exists for this dimension"`
 * if both are missing. Pass `value` defensively when you're not sure.
 *
 * Does not touch `confirmed_count` / `confirmed_at` — pinning preserves
 * the existing value, so prior confirmations still apply.
 */
export async function pinMemoryFact(
  userId: string,
  dimension: ProfileDimension | string,
  value?: unknown,
): Promise<{ success: boolean; override: MemoryOverride }> {
  return postMemoryAction(userId, 'pin', { dimension, value })
}

/**
 * VOY-458: delete the override AND tombstone the rejected value. This is
 * a NEGATIVE signal — the agent reads the tombstone and will not re-derive
 * the same (dimension, value) pair without strong new evidence.
 *
 * `current_value` lets the client pass the value being rejected even when
 * there's no override row yet (e.g. rejecting a pure AI inference). When
 * omitted, the backend reads the prior override value.
 */
export async function deleteMemoryFact(
  userId: string,
  dimension: ProfileDimension | string,
  opts: { currentValue?: unknown; reason?: string } = {},
): Promise<{ success: boolean; deleted: true }> {
  return postMemoryAction(userId, 'delete', {
    dimension,
    current_value: opts.currentValue,
    reason: opts.reason,
  })
}

// =============================================================================
// VOY-459: Traveler Notes — backend-only ticket. These helpers wrap the four
// CRUD endpoints at /api/v2/profile/:userId/notes/*. UI (NotesSection,
// AddNoteForm) ships with VOY-417 per the scope decision; this PR provides
// the typed wire contract VOY-417 will call.
// =============================================================================

export interface CreateTravelerNoteInput {
  content: string
  scope: NoteScope
  importance?: number
  related_companion_id?: string
  related_trip_id?: string
}

/** Mutable subset of TravelerNote that PATCH accepts. */
export type UpdateTravelerNoteInput = Partial<{
  content: string
  scope: NoteScope
  importance: number
  related_companion_id: string | null
  related_trip_id: string | null
}>

/**
 * VOY-459: list active traveler notes for a user, optionally filtered by
 * scope. Backend orders by importance DESC, created_at DESC.
 *
 * Throws `AgentContextError` on non-2xx for consistency with the other
 * profile fetchers.
 */
export async function getTravelerNotes(
  userId: string,
  opts: { scope?: NoteScope } = {},
): Promise<TravelerNote[]> {
  const qs = opts.scope ? `?scope=${encodeURIComponent(opts.scope)}` : ''
  const res = await apiFetch(`/api/v2/profile/${userId}/notes${qs}`)
  if (res.ok) {
    const body = await res.json()
    return (body?.notes ?? []) as TravelerNote[]
  }
  const errBody = await res.json().catch(() => ({}))
  throw new AgentContextError(
    errBody?.error || `notes list failed (${res.status})`,
    res.status,
  )
}

/**
 * VOY-459: create a new note. Backend defaults importance to 3 when omitted.
 * `companion_specific` requires `related_companion_id`; `trip_specific`
 * requires `related_trip_id` — backend returns 400 otherwise.
 */
export async function createTravelerNote(
  userId: string,
  input: CreateTravelerNoteInput,
): Promise<TravelerNote> {
  const res = await apiFetch(`/api/v2/profile/${userId}/notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (res.ok) {
    const body = await res.json()
    return body.note as TravelerNote
  }
  const errBody = await res.json().catch(() => ({}))
  throw new AgentContextError(
    errBody?.error || `note create failed (${res.status})`,
    res.status,
  )
}

/**
 * VOY-459: partial update of a single note. At least one mutable field
 * must be supplied — backend 400s otherwise.
 */
export async function updateTravelerNote(
  userId: string,
  noteId: string,
  patch: UpdateTravelerNoteInput,
): Promise<TravelerNote> {
  const res = await apiFetch(`/api/v2/profile/${userId}/notes/${noteId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  })
  if (res.ok) {
    const body = await res.json()
    return body.note as TravelerNote
  }
  const errBody = await res.json().catch(() => ({}))
  throw new AgentContextError(
    errBody?.error || `note update failed (${res.status})`,
    res.status,
  )
}

/**
 * VOY-459: soft-delete a note (sets active=false). The row is retained so
 * future agent passes can see "the user retracted this constraint on date
 * X" — there is intentionally no hard-delete endpoint.
 */
export async function deleteTravelerNote(
  userId: string,
  noteId: string,
): Promise<{ success: boolean; deleted: true }> {
  const res = await apiFetch(`/api/v2/profile/${userId}/notes/${noteId}`, {
    method: 'DELETE',
  })
  if (res.ok) {
    return (await res.json()) as { success: boolean; deleted: true }
  }
  const errBody = await res.json().catch(() => ({}))
  throw new AgentContextError(
    errBody?.error || `note delete failed (${res.status})`,
    res.status,
  )
}

function formatCategoryName(category: string): string {
  const categoryNames: Record<string, string> = {
    adventure: 'Adventure Seeker',
    culture: 'Culture Explorer',
    relaxation: 'Relaxation Enthusiast',
    urban: 'City Explorer',
    structured: 'Meticulous Planner',
    flexible: 'Flexible Traveler',
    spontaneous: 'Spontaneous Wanderer',
    balanced: 'Balanced Explorer',
    luxury: 'Luxury Traveler',
    boutique: 'Boutique Enthusiast',
    authentic: 'Authentic Experiencer',
    budget: 'Budget Adventurer',
    foodie: 'Culinary Connoisseur',
    culinary: 'Food Explorer',
    explorer: 'Curious Explorer',
    practical: 'Practical Traveler',
    intensive: 'Intensive Explorer',
    immersive: 'Deep Diver',
    leisurely: 'Leisure Seeker'
  }
  
  return categoryNames[category] || category
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

function generateProfileDescription(primary: string, secondary: string | null): string {
  const descriptions: Record<string, string> = {
    adventure: 'You thrive on adrenaline and seek out thrilling experiences wherever you go.',
    culture: 'You\'re passionate about understanding local traditions and immersing yourself in history.',
    relaxation: 'You travel to unwind and recharge, preferring peaceful destinations and spa experiences.',
    urban: 'You love the energy of big cities and enjoy exploring metropolitan areas.',
    luxury: 'You appreciate the finer things in travel and seek out premium experiences.',
    foodie: 'Cuisine is central to your travel experience, and you plan trips around culinary adventures.',
    spontaneous: 'You embrace the unexpected and love the freedom of unplanned adventures.'
  }
  
  let description = descriptions[primary] || 'You have a unique travel style that combines various interests.'
  
  if (secondary && descriptions[secondary]) {
    description += ' ' + descriptions[secondary]
  }
  
  return description
}

// Note: Keyword functions have been consolidated with getKeywordPreferences and saveKeywordPreferences above
// These legacy functions are removed to avoid confusion

// =============================================================================
// VOY-460: Travel Companions — backend-only ticket. These helpers wrap the
// 4 CRUD endpoints at /api/v2/profile/:userId/companions/*. UI (CompanionsCard
// in profile, CompanionPicker in search) is deferred — CompanionsCard belongs
// to VOY-485, CompanionPicker to VOY-461.
// =============================================================================

/**
 * VOY-460 PR review: `voy_account_user_id` is intentionally absent from
 * this input — the backend strips it from POST/PATCH bodies because it's
 * a forward-compat link a future "claim your own Voy account" flow will
 * set server-side. The field still appears on the returned
 * `TravelCompanion` (the backend may eventually populate it), but it is
 * not user-writable.
 */
export interface CreateTravelCompanionInput {
  display_name: string
  relationship?: Relationship | null
  date_of_birth?: string | null  // YYYY-MM-DD
  dietary_restrictions?: string[]
  mobility_notes?: string | null  // ≤ 500 chars
  passport_country?: string | null  // 2- or 3-letter; normalised upper-case server-side
  passport_expiry?: string | null   // YYYY-MM-DD
  loyalty_programs?: unknown[]
  trip_frequency_per_year?: number | null
}

/** Mutable subset of TravelCompanion that PATCH accepts. */
export type UpdateTravelCompanionInput = Partial<CreateTravelCompanionInput>

/**
 * VOY-460: list active companions for a user. Backend orders by
 * `created_at DESC`. Throws `AgentContextError` on non-2xx for
 * consistency with the other profile fetchers.
 */
export async function getTravelCompanions(userId: string): Promise<TravelCompanion[]> {
  const res = await apiFetch(`/api/v2/profile/${userId}/companions`)
  if (res.ok) {
    const body = await res.json()
    return (body?.companions ?? []) as TravelCompanion[]
  }
  const errBody = await res.json().catch(() => ({}))
  throw new AgentContextError(
    errBody?.error || `companions list failed (${res.status})`,
    res.status,
  )
}

/**
 * VOY-460: fetch a single companion by id. Returns `null` on 404
 * (companion doesn't exist or has been soft-deleted). Added in the
 * PR review pass for VOY-485's CompanionEditModal which fetches by id.
 */
export async function getTravelCompanion(
  userId: string,
  companionId: string,
): Promise<TravelCompanion | null> {
  const res = await apiFetch(`/api/v2/profile/${userId}/companions/${companionId}`)
  if (res.ok) {
    const body = await res.json()
    return (body?.companion ?? null) as TravelCompanion | null
  }
  if (res.status === 404) return null
  const errBody = await res.json().catch(() => ({}))
  throw new AgentContextError(
    errBody?.error || `companion get failed (${res.status})`,
    res.status,
  )
}

/**
 * VOY-460: create a companion. `display_name` is the only required field;
 * everything else is optional. Date strings must be `YYYY-MM-DD`.
 * `passport_country` accepts 2- or 3-letter ISO codes.
 */
export async function createTravelCompanion(
  userId: string,
  input: CreateTravelCompanionInput,
): Promise<TravelCompanion> {
  const res = await apiFetch(`/api/v2/profile/${userId}/companions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (res.ok) {
    const body = await res.json()
    return body.companion as TravelCompanion
  }
  const errBody = await res.json().catch(() => ({}))
  throw new AgentContextError(
    errBody?.error || `companion create failed (${res.status})`,
    res.status,
  )
}

/**
 * VOY-460: partial update of a single companion. At least one mutable
 * field is required — backend 400s on an empty patch. PATCH on a
 * soft-deleted companion returns 404.
 */
export async function updateTravelCompanion(
  userId: string,
  companionId: string,
  patch: UpdateTravelCompanionInput,
): Promise<TravelCompanion> {
  const res = await apiFetch(`/api/v2/profile/${userId}/companions/${companionId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  })
  if (res.ok) {
    const body = await res.json()
    return body.companion as TravelCompanion
  }
  const errBody = await res.json().catch(() => ({}))
  throw new AgentContextError(
    errBody?.error || `companion update failed (${res.status})`,
    res.status,
  )
}

/**
 * VOY-460: soft-delete a companion (sets active=false). Hard-delete is
 * intentionally not exposed because the FK from `traveler_notes` cascades
 * — VOY-465 (privacy controls) will own any explicit hard-delete flow.
 */
export async function deleteTravelCompanion(
  userId: string,
  companionId: string,
): Promise<{ success: boolean; deleted: true }> {
  const res = await apiFetch(`/api/v2/profile/${userId}/companions/${companionId}`, {
    method: 'DELETE',
  })
  if (res.ok) {
    return (await res.json()) as { success: boolean; deleted: true }
  }
  const errBody = await res.json().catch(() => ({}))
  throw new AgentContextError(
    errBody?.error || `companion delete failed (${res.status})`,
    res.status,
  )
}

// =============================================================================
// VOY-465: Privacy controls — backend-driven export / wipe / account delete.
// Helpers throw `AgentContextError` on non-2xx for consistency with the rest
// of the profile fetcher family. The destructive helpers (wipeMemory,
// deleteAccount) take a `password` argument that the backend verifies via a
// fresh Supabase re-auth before doing anything irreversible.
// =============================================================================

/**
 * VOY-465: trigger the memory export download. Hits
 * `GET /api/v2/profile/:userId/memory/export` and returns the JSON `Blob`
 * the backend ships as an attachment. Caller is responsible for handing
 * the blob to the browser (e.g. via createObjectURL + an anchor click).
 *
 * Returning a Blob (not the parsed object) preserves the original byte
 * sequence so the file the user saves matches what the backend
 * generated — important for compliance audits.
 */
export async function exportMemory(userId: string): Promise<{ blob: Blob; filename: string }> {
  const res = await apiFetch(`/api/v2/profile/${userId}/memory/export`)
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}))
    throw new AgentContextError(
      errBody?.error || `memory export failed (${res.status})`,
      res.status,
    )
  }
  const blob = await res.blob()
  // Pull filename from Content-Disposition; fall back to a sensible
  // default if the header isn't present (shouldn't happen but defensive).
  const dispo = res.headers.get('Content-Disposition') || ''
  const match = /filename="([^"]+)"/.exec(dispo)
  const today = new Date().toISOString().slice(0, 10)
  const filename = match?.[1] || `voy-memory-export-${userId}-${today}.json`
  return { blob, filename }
}

/**
 * VOY-465: convenience wrapper around exportMemory() that downloads the
 * file via a temporary anchor element. Most UI consumers (including
 * PrivacyControls) want this end-to-end behaviour, not the Blob.
 */
export async function downloadMemoryExport(userId: string): Promise<void> {
  const { blob, filename } = await exportMemory(userId)
  const url = URL.createObjectURL(blob)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
  } finally {
    // Defer revocation to the next tick so the download has actually
    // started; some browsers race on immediate revoke.
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }
}

/**
 * VOY-465: wipe the user's memory. Requires re-auth — backend verifies
 * `password` against Supabase before touching anything.
 *
 * Throws `AgentContextError` with status `401` if the password is wrong,
 * `403` if the URL :userId doesn't match the authed user, `500` on any
 * other failure. Per-table delete counts are returned in `.wiped` for the
 * UI's "we deleted N notes, M companions, …" confirmation toast.
 */
export async function wipeMemory(userId: string, password: string): Promise<MemoryWipeResult> {
  const res = await apiFetch(`/api/v2/profile/${userId}/memory/wipe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  })
  if (res.ok) return (await res.json()) as MemoryWipeResult
  const errBody = await res.json().catch(() => ({}))
  throw new AgentContextError(
    errBody?.error || `memory wipe failed (${res.status})`,
    res.status,
  )
}

/**
 * VOY-465: schedule the user's account for deletion. Wipes memory
 * immediately and flips `profiles.status` to `'pending_deletion'` with a
 * 7-day grace window. Actual `auth.users` deletion happens via a daily
 * pg_cron sweeper after the window elapses. Recoverable via
 * `restoreAccount` until the sweeper runs.
 *
 * Requires the same password re-auth as `wipeMemory`.
 */
export async function deleteAccount(userId: string, password: string): Promise<AccountDeleteResult> {
  const res = await apiFetch(`/api/v2/profile/${userId}/account/delete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  })
  if (res.ok) return (await res.json()) as AccountDeleteResult
  const errBody = await res.json().catch(() => ({}))
  throw new AgentContextError(
    errBody?.error || `account delete failed (${res.status})`,
    res.status,
  )
}

/**
 * VOY-465: cancel a pending deletion. Flips `status` back to `'active'`
 * and clears `pending_deletion_at`. Does NOT restore wiped memory — the
 * wipe was destructive by design.
 *
 * Returns `410` (Gone) if the grace window has already lapsed and the
 * sweeper has run / will run imminently, `409` if the account isn't
 * actually pending. No re-auth required — restoring is non-destructive
 * and the ensureSelf gate prevents cross-user calls.
 */
export async function restoreAccount(userId: string): Promise<AccountRestoreResult> {
  const res = await apiFetch(`/api/v2/profile/${userId}/account/restore`, {
    method: 'POST',
  })
  if (res.ok) return (await res.json()) as AccountRestoreResult
  const errBody = await res.json().catch(() => ({}))
  throw new AgentContextError(
    errBody?.error || `account restore failed (${res.status})`,
    res.status,
  )
}

// =====================================================================
// VOY-485 (rebuild) — booking aggregations for Profile / Traveler stats
//                   + Places visited
// =====================================================================
//
// Source: `orders` (status='confirmed') joined to `order_items.item_data`.
// Every item_data shape we ship today (Duffel slice / RateHawk hotel /
// other) carries a top-level `destination` string — the only field
// guaranteed across suppliers. Cities are deduped on that. Country code is
// best-effort (small IATA→ISO + city→ISO dictionaries); unknown → null.
// VOY-485 TODO: when the order pipeline captures a clean
// `destination_country_code`, swap the heuristic for that field.

export interface TravelerStats {
  countries_visited: number
  cities_visited: number
  trips_booked: number
  /**
   * `null` while VOY-420 (savings calculation) is unshipped. UI renders a
   * graceful placeholder for this stat; do NOT fabricate a number. When
   * VOY-420 lands, expose `{ amount: number, currency: string }`.
   */
  saved_with_voy: null
}

export interface PlaceVisited {
  /** City name as captured by the supplier (the raw `item_data.destination`). */
  city: string
  /** ISO 3166-1 alpha-2 country code when derivable, else `null`. */
  country_code: string | null
  /** Number of confirmed bookings whose destination matched this city. */
  visit_count: number
}

const IATA_TO_COUNTRY: Record<string, string> = {
  LIS: 'PT', OPO: 'PT', MAD: 'ES', BCN: 'ES',
  CDG: 'FR', ORY: 'FR', LHR: 'GB', LGW: 'GB', LCY: 'GB',
  FCO: 'IT', MXP: 'IT', LIN: 'IT', VCE: 'IT',
  AMS: 'NL', BRU: 'BE', CPH: 'DK', ARN: 'SE', OSL: 'NO',
  FRA: 'DE', MUC: 'DE', BER: 'DE', ZRH: 'CH', VIE: 'AT',
  JFK: 'US', LGA: 'US', EWR: 'US', LAX: 'US', SFO: 'US', MIA: 'US', ORD: 'US', BOS: 'US', SEA: 'US',
  YYZ: 'CA', YVR: 'CA', YUL: 'CA',
  MEX: 'MX', CUN: 'MX',
  EZE: 'AR', AEP: 'AR', SCL: 'CL', GRU: 'BR', GIG: 'BR', LIM: 'PE',
  NRT: 'JP', HND: 'JP', KIX: 'JP',
  ICN: 'KR', PEK: 'CN', PVG: 'CN', HKG: 'HK', SIN: 'SG', BKK: 'TH', DPS: 'ID',
  SYD: 'AU', MEL: 'AU', AKL: 'NZ',
  CAI: 'EG', JNB: 'ZA', CPT: 'ZA', RAK: 'MA', CMN: 'MA', DXB: 'AE', AUH: 'AE',
}

const CITY_TO_COUNTRY: Record<string, string> = {
  lisbon: 'PT', porto: 'PT', madrid: 'ES', barcelona: 'ES',
  paris: 'FR', london: 'GB', rome: 'IT', milan: 'IT', venice: 'IT',
  amsterdam: 'NL', brussels: 'BE', copenhagen: 'DK', stockholm: 'SE', oslo: 'NO',
  berlin: 'DE', munich: 'DE', frankfurt: 'DE', zurich: 'CH', vienna: 'AT',
  'new york': 'US', 'los angeles': 'US', miami: 'US', 'miami beach': 'US',
  chicago: 'US', boston: 'US', seattle: 'US', 'san francisco': 'US',
  toronto: 'CA', vancouver: 'CA', montreal: 'CA',
  'mexico city': 'MX', cancun: 'MX',
  'buenos aires': 'AR', santiago: 'CL', 'sao paulo': 'BR', rio: 'BR', lima: 'PE',
  tokyo: 'JP', osaka: 'JP', seoul: 'KR', beijing: 'CN', shanghai: 'CN',
  'hong kong': 'HK', singapore: 'SG', bangkok: 'TH', bali: 'ID',
  sydney: 'AU', melbourne: 'AU', auckland: 'NZ',
  cairo: 'EG', johannesburg: 'ZA', 'cape town': 'ZA', marrakech: 'MA', casablanca: 'MA',
  dubai: 'AE',
  madeira: 'PT',
}

function deriveCountryCode(rawDestination: string | null | undefined): string | null {
  if (!rawDestination) return null
  const trimmed = String(rawDestination).trim()
  if (!trimmed) return null
  if (/^[A-Z]{3}$/.test(trimmed)) return IATA_TO_COUNTRY[trimmed] ?? null
  return CITY_TO_COUNTRY[trimmed.toLowerCase()] ?? null
}

interface OrderItemRowForAggregation {
  item_data: { destination?: string | null } | null
  orders: { user_id: string; status: string | null; confirmed_at: string | null } | null
}

/**
 * VOY-485 — aggregate Traveler-stats counters from confirmed orders.
 * Errors degrade to zeroes so the Profile page never fails to render.
 */
export async function getTravelerStats(userId: string): Promise<TravelerStats> {
  if (!userId) {
    return { countries_visited: 0, cities_visited: 0, trips_booked: 0, saved_with_voy: null }
  }
  try {
    const { data, error } = await supabase
      .from('order_items')
      .select('item_data, orders!inner(user_id, status, confirmed_at)')
      .eq('orders.user_id', userId)
      .eq('orders.status', 'confirmed')
    if (error) {
      console.error('[getTravelerStats] query failed:', error)
      return { countries_visited: 0, cities_visited: 0, trips_booked: 0, saved_with_voy: null }
    }
    const rows = (data || []) as unknown as OrderItemRowForAggregation[]
    const citySet = new Set<string>()
    const countrySet = new Set<string>()
    for (const row of rows) {
      const dest = row?.item_data?.destination
      if (dest && typeof dest === 'string') {
        const trimmed = dest.trim()
        if (trimmed) {
          citySet.add(trimmed.toLowerCase())
          const cc = deriveCountryCode(trimmed)
          if (cc) countrySet.add(cc)
        }
      }
    }
    const { count: orderCount } = await supabase
      .from('orders')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('status', 'confirmed')
    return {
      countries_visited: countrySet.size,
      cities_visited: citySet.size,
      trips_booked: orderCount ?? 0,
      saved_with_voy: null,
    }
  } catch (err) {
    console.error('[getTravelerStats] unexpected:', err)
    return { countries_visited: 0, cities_visited: 0, trips_booked: 0, saved_with_voy: null }
  }
}

/**
 * VOY-485 — places visited in the trailing 12 months, deduped and sorted
 * by visit count descending. Confirmed orders only. Default cap of 24.
 */
export async function getPlacesVisited(userId: string, limit = 24): Promise<PlaceVisited[]> {
  if (!userId) return []
  try {
    const since = new Date()
    since.setMonth(since.getMonth() - 12)
    const { data, error } = await supabase
      .from('order_items')
      .select('item_data, orders!inner(user_id, status, confirmed_at)')
      .eq('orders.user_id', userId)
      .eq('orders.status', 'confirmed')
      .gte('orders.confirmed_at', since.toISOString())
    if (error) {
      console.error('[getPlacesVisited] query failed:', error)
      return []
    }
    const rows = (data || []) as unknown as OrderItemRowForAggregation[]
    const byKey = new Map<string, { display: string; country_code: string | null; count: number }>()
    for (const row of rows) {
      const raw = row?.item_data?.destination
      if (!raw || typeof raw !== 'string') continue
      const trimmed = raw.trim()
      if (!trimmed) continue
      const key = trimmed.toLowerCase()
      const existing = byKey.get(key)
      if (existing) existing.count += 1
      else byKey.set(key, { display: trimmed, country_code: deriveCountryCode(trimmed), count: 1 })
    }
    return Array.from(byKey.values())
      .map((v) => ({ city: v.display, country_code: v.country_code, visit_count: v.count }))
      .sort((a, b) => b.visit_count - a.visit_count || a.city.localeCompare(b.city))
      .slice(0, limit)
  } catch (err) {
    console.error('[getPlacesVisited] unexpected:', err)
    return []
  }
}

// =====================================================================
// VOY-485 (rebuild) — Recent orders/purchases viewer
// =====================================================================
//
// Source: `orders` + `order_items`, no `status` filter (the Profile page
// shows pending + confirmed + failed alike so the user can see in-flight
// work). Direct Supabase query — same as src/pages/profile/purchases.tsx
// which is functional but route-orphaned. The Profile rebuild surfaces a
// recent-N slice inline; the full receipt-style view at /profile/purchases
// remains accessible via a "View all" link (and we register that route in
// App.tsx as part of this PR).

export interface RecentOrderItem {
  id: string
  item_type: string
  status: string | null
  total_price: number | null
  currency: string | null
  /**
   * Raw item_data — Profile rebuild only uses `destination` + `hotel_name` /
   * `airline` for the summary line. Typed `unknown` here so callers don't
   * touch the rest without an explicit narrowing.
   */
  item_data: unknown
}

export interface RecentOrder {
  id: string
  status: string | null
  total_amount: number | null
  currency: string | null
  created_at: string
  confirmed_at: string | null
  order_items: RecentOrderItem[]
}

export async function getRecentOrders(userId: string, limit = 5): Promise<RecentOrder[]> {
  if (!userId) return []
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('id, status, total_amount, currency, created_at, confirmed_at, order_items(id, item_type, status, total_price, currency, item_data)')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit)
    if (error) {
      console.error('[getRecentOrders] query failed:', error)
      return []
    }
    return (data as unknown as RecentOrder[]) || []
  } catch (err) {
    console.error('[getRecentOrders] unexpected:', err)
    return []
  }
}

// =====================================================================
// VOY-417 — Signals over time (You page §5)
// =====================================================================
//
// DB-computed, NOT agent output. Four stats over the trailing 12 months:
//   1. avg_trip_spend ($) + bookings_count
//   2. saved_by_voy ($) — STUB until VOY-420 (pricing module) ships
//   3. top_destination + visit_count
//   4. search_to_book_rate (conversions / total searches)
//
// Source tables: orders (confirmed), order_items, saved_searches.
// Errors degrade to nulls so the section renders empty states rather
// than failing the whole page.

export interface SignalStats {
  /** Average total_amount across confirmed orders. `null` when no bookings. */
  avg_trip_spend: { amount: number; currency: string } | null
  bookings_count: number
  /** `null` until VOY-420 lands; UI renders a PENDING stub. */
  saved_by_voy: { amount: number; currency: string } | null
  /** Most-visited destination by booking count. `null` when no bookings. */
  top_destination: { city: string; country_code: string | null; visit_count: number } | null
  /** Searches → confirmed-orders ratio. `null` when no searches. */
  search_to_book_rate: number | null
  total_searches: number
}

interface OrderRowForSignals {
  total_amount: number | null
  currency: string | null
  confirmed_at: string | null
}

function dominantCurrency(currencies: Array<string | null | undefined>): string | null {
  const counts = new Map<string, number>()
  for (const c of currencies) {
    if (!c) continue
    counts.set(c, (counts.get(c) || 0) + 1)
  }
  if (counts.size === 0) return null
  return Array.from(counts.entries()).sort((a, b) => b[1] - a[1])[0][0]
}

export async function getSignalStats(userId: string): Promise<SignalStats> {
  const empty: SignalStats = {
    avg_trip_spend: null,
    bookings_count: 0,
    saved_by_voy: null,
    top_destination: null,
    search_to_book_rate: null,
    total_searches: 0,
  }
  if (!userId) return empty

  try {
    const since = new Date()
    since.setMonth(since.getMonth() - 12)
    const sinceIso = since.toISOString()

    // Three parallel queries — same direct-Supabase pattern as
    // getRecentOrders + getPlacesVisited.
    const [ordersRes, itemsRes, searchesRes] = await Promise.all([
      supabase
        .from('orders')
        .select('total_amount, currency, confirmed_at')
        .eq('user_id', userId)
        .eq('status', 'confirmed')
        .gte('confirmed_at', sinceIso),
      supabase
        .from('order_items')
        .select('item_data, orders!inner(user_id, status, confirmed_at)')
        .eq('orders.user_id', userId)
        .eq('orders.status', 'confirmed')
        .gte('orders.confirmed_at', sinceIso),
      supabase
        .from('saved_searches')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .gte('created_at', sinceIso),
    ])

    // Avg trip spend
    const orderRows = (ordersRes.data || []) as OrderRowForSignals[]
    const ordersWithAmount = orderRows.filter((o): o is OrderRowForSignals & { total_amount: number } =>
      typeof o.total_amount === 'number' && o.total_amount > 0,
    )
    const avg_trip_spend = ordersWithAmount.length
      ? {
          amount:
            ordersWithAmount.reduce((sum, o) => sum + o.total_amount, 0) /
            ordersWithAmount.length,
          // Dominant currency in the result set — mixed currencies would
          // need an FX layer (out of scope until VOY-420).
          currency: dominantCurrency(ordersWithAmount.map((o) => o.currency)) || 'USD',
        }
      : null

    // Top destination
    const itemRows = (itemsRes.data || []) as unknown as Array<{
      item_data: { destination?: string | null } | null
    }>
    const byKey = new Map<string, { display: string; count: number }>()
    for (const row of itemRows) {
      const raw = row?.item_data?.destination
      if (!raw || typeof raw !== 'string') continue
      const trimmed = raw.trim()
      if (!trimmed) continue
      const key = trimmed.toLowerCase()
      const ex = byKey.get(key)
      if (ex) ex.count += 1
      else byKey.set(key, { display: trimmed, count: 1 })
    }
    let top_destination: SignalStats['top_destination'] = null
    if (byKey.size > 0) {
      const best = Array.from(byKey.values()).sort((a, b) => b.count - a.count)[0]
      top_destination = {
        city: best.display,
        country_code: deriveCountryCode(best.display),
        visit_count: best.count,
      }
    }

    // Search → book rate
    const total_searches = searchesRes.count ?? 0
    const bookings_count = orderRows.length
    const search_to_book_rate = total_searches > 0
      ? Number((bookings_count / total_searches).toFixed(3))
      : null

    return {
      avg_trip_spend,
      bookings_count,
      saved_by_voy: null,  // VOY-420 stub
      top_destination,
      search_to_book_rate,
      total_searches,
    }
  } catch (err) {
    console.error('[getSignalStats] unexpected:', err)
    return empty
  }
}
